import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function GlossyNav({ dark = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onDark = dark && !scrolled;
  const textColor = onDark ? "text-cream" : "text-ink";
  const links = [
    { to: "/the-edit", label: "The Edit" },
    { to: "/interviews", label: "Interviews" },
    { to: "/cover-members", label: "Cover Members" },
    { to: "/directory", label: "Directory" },
    { to: "/noticeboard", label: "Noticeboard" },
    { to: "/members-desk", label: "Members' Desk" },
    { to: "/about", label: "About" },
    { to: "/join", label: "Join" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/90 backdrop-blur-xl border-b border-ink/10" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between gap-x-8">
        <Link to="/" className={`font-serif text-2xl sm:text-3xl tracking-tight shrink-0 ${scrolled ? "text-ink" : textColor}`} data-testid="nav-logo">
          S/ME
        </Link>

        <nav className="hidden lg:flex items-center gap-x-4 xl:gap-x-6">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`overline link-underline whitespace-nowrap ${scrolled ? "text-ink" : textColor}`}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <div className="flex items-center gap-4">
              <Link to="/portal" className={`overline link-underline whitespace-nowrap ${scrolled ? "text-ink" : textColor}`} data-testid="nav-portal">
                Portal
              </Link>
              <button
                onClick={() => { logout(); navigate("/"); }}
                className="overline border border-current px-4 py-2 hover:bg-ink hover:text-cream transition-colors text-ink whitespace-nowrap"
                data-testid="nav-logout"
              >
                Sign out
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className={`overline px-5 py-2.5 whitespace-nowrap transition-colors ${
                scrolled ? "bg-ink text-cream hover:bg-ink" : "bg-cream text-ink hover:bg-ink hover:text-cream"
              }`}
              data-testid="nav-login"
            >
              Sign in
            </Link>
          )}
        </nav>

        <button className={`lg:hidden ${scrolled ? "text-ink" : textColor}`} onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-ink/10 px-5 py-6 flex flex-col gap-5" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="overline text-ink">
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/portal" onClick={() => setOpen(false)} className="overline text-ink">Portal</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="overline text-left text-gloss-silver">Sign out</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="overline text-ink">Sign in</Link>
          )}
        </div>
      )}
    </header>
  );
}
